import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { TIMEZONE } from '@/lib/format';
import type { SeasonalityMode } from './ChartModeToggle';

/**
 * Collapse a chronological time-series into a periodicity view:
 *
 *   - 'hour-of-day' → 24 buckets keyed by `00..23`, summing each numeric
 *                     metric across every datapoint that falls in that hour
 *                     (in `Asia/Kathmandu`, the same wall-clock the rest of
 *                     the BI surface uses).
 *   - 'day-of-week' → 7 buckets keyed by `Sun..Sat`, same rollup rule.
 *
 * The output mirrors the input row shape so the existing `<LineChart>`
 * (`xKey`, `series` props) just works — the only difference is the x value
 * becomes a categorical label instead of an ISO timestamp.
 *
 * Caller decides which numeric keys to fold; everything else is dropped.
 */
export interface SeasonalityRow {
  bucket: string;
  /** Number of source datapoints folded into this bucket. */
  samples: number;
  [k: string]: string | number | null;
}

const HOUR_LABELS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export function bucketBySeasonality<T extends object>(
  rows: T[],
  opts: {
    timeKey: keyof T & string;
    metrics: (keyof T & string)[];
    mode: Exclude<SeasonalityMode, 'none'>;
    /** How to combine values within a bucket. Defaults to `sum`. */
    aggregate?: 'sum' | 'avg';
  },
): SeasonalityRow[] {
  const labels = opts.mode === 'hour-of-day' ? HOUR_LABELS : DOW_LABELS;
  const indexFor = (d: Date) => {
    if (opts.mode === 'hour-of-day') {
      // Use the timezone-corrected hour so the buckets line up with what
      // Kathmandu users see on the wall clock.
      const hour = Number(formatInTimeZone(d, TIMEZONE, 'H'));
      return Number.isFinite(hour) ? hour : 0;
    }
    // date-fns getDay returns 0..6 (Sun=0). We want the *zoned* day-of-week.
    const zoned = toZonedTime(d, TIMEZONE);
    return zoned.getDay();
  };

  // Pre-seed every label with zeros so the chart always shows the full axis,
  // even when the data only covers a subset of hours/days.
  const sums = new Map<string, Record<string, number>>();
  const counts = new Map<string, number>();
  for (const label of labels) {
    const init: Record<string, number> = {};
    for (const m of opts.metrics) init[m] = 0;
    sums.set(label, init);
    counts.set(label, 0);
  }

  for (const row of rows) {
    const raw = (row as Record<string, unknown>)[opts.timeKey];
    if (typeof raw !== 'string' && !(raw instanceof Date)) continue;
    const d = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(d.getTime())) continue;
    const bucket = labels[indexFor(d)];
    const acc = sums.get(bucket)!;
    for (const m of opts.metrics) {
      const v = Number((row as Record<string, unknown>)[m]);
      if (Number.isFinite(v)) acc[m] += v;
    }
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }

  return labels.map((label) => {
    const acc = sums.get(label)!;
    const n = counts.get(label) ?? 0;
    const out: SeasonalityRow = { bucket: label, samples: n };
    for (const m of opts.metrics) {
      out[m] = opts.aggregate === 'avg' && n > 0 ? acc[m] / n : acc[m];
    }
    return out;
  });
}

/**
 * Recommend which granularities make sense for a given date range.
 *
 * Historically we disabled `hour` for ranges wider than ~7 days (and `week`
 * for short ranges) to prevent noisy charts. That was too aggressive: it
 * broke the **Hour-of-day** seasonality view, which *requires* hour-bucket
 * data to roll up correctly, and it confused users who explicitly wanted
 * to scrub a multi-day window at hourly resolution. The backend caps the
 * window at 400 days so the volume risk is bounded; we now leave the
 * choice to the user.
 *
 * Kept as an exported function (returning `[]`) so existing callers can
 * stay unchanged.
 */
export function disabledGranularitiesFor(_from: Date, _to: Date): ('hour' | 'day' | 'week')[] {
  return [];
}

/**
 * Map a seasonality mode to the underlying server granularity required to
 * roll it up correctly on the client. `hour-of-day` needs hour buckets so
 * each datapoint contributes to a specific hour; `day-of-week` needs at
 * least day buckets. Returns `null` for `'none'` (no opinion).
 */
export function granularityForSeasonality(
  mode: SeasonalityMode,
): 'hour' | 'day' | null {
  if (mode === 'hour-of-day') return 'hour';
  if (mode === 'day-of-week') return 'day';
  return null;
}
